import { fromEvent } from 'rxjs';
import { closeModalIconSvgElement } from '../constants/svgIcons';

export default class InnerModal {
  content = '';

  selector = '[data-self-guided-tour-inner-modal]';

  primaryButtonText = 'OK';

  secondaryButtonText = 'Cancel';

  primaryButtonAction = undefined;

  secondaryButtonAction = undefined;

  primaryButtonSubscription$ = null;

  secondaryButtonSubscription$ = null;

  closeButtonSubscription$ = null;

  backgroundOverlaySubscription$ = null;

  secondaryButtonCssClass = '';

  closeModalIconSvgElement = closeModalIconSvgElement;

  secondaryButtonDefaultCssClasses = [
    'nhs-c-button--secondary',
    'nhs-c-button--text-only',
    'nhs-o-flex-row-items-center-justify-center',
    'nhs-c-button--large-48',
    'nhs-c-button--full',
  ];

  primaryButtonDefaultCssClasses = [
    'nhs-c-button--primary-ui',
    'nhs-c-button--text-only',
    'nhs-o-flex-row-items-center-justify-center',
    'nhs-c-button--large-48',
    'nhs-c-button--full'
  ];

  constructor(showCallback, hideCallback) {
    this.showCallback = showCallback;
    this.hideCallback = hideCallback;
  }

  getPrimaryButton() {
    return this.getNode()?.querySelector('[data-self-guided-tour-inner-modal-primary-button]');
  }

  getSecondaryButton() {
    return this.getNode()?.querySelector('[data-self-guided-tour-inner-modal-secondary-button]');
  }

  getCloseButton() {
    return this.getNode()?.querySelector('[data-self-guided-tour-inner-modal-close]');
  }

  getNode() {
    return document.querySelector(this.selector);
  }

  /**
   * Displays the modal
   */
  show() {
    const modal = this.getNode();
    if (modal) {
      modal.hidden = false;
      if (typeof this.showCallback === 'function') {
        this.showCallback();
      }
    }
  }

  /**
   * Hides the modal by setting its display style to 'none'.
   */
  hide() {
    const modal = this.getNode();
    if (modal) {
      modal.hidden = true;
      if (typeof this.hideCallback === 'function') {
        this.hideCallback();
      }
    }
  }

  /**
   * Binds events to the modal buttons and background overlay.
   */
  bindEvents() {
    this.primaryButtonSubscription$ = fromEvent(this.getPrimaryButton(), 'click').subscribe(this.primaryButtonAction);
    const secondaryButton = this.getSecondaryButton();
    if (secondaryButton) {
      this.secondaryButtonSubscription$ = fromEvent(secondaryButton, 'click').subscribe(() => {
        if (typeof this.secondaryButtonAction === 'function') {
          this.secondaryButtonAction();
          return;
        }
        this.hide();
      });
    }

    this.closeButtonSubscription$ = fromEvent(this.getCloseButton(), 'click').subscribe(this.hide.bind(this));

    this.backgroundOverlaySubscription$ = fromEvent(this.getNode(), 'click').subscribe((event) => {
      // we need to make sure that the click is not on the modal itself, only on the background overlay
      if (event.target === this.getNode()) {
        this.hide();
      }
    });
  }

  /**
   * Unbinds all events related to the modal.
   */
  unbindEvents() {
    this.primaryButtonSubscription$?.unsubscribe();
    this.secondaryButtonSubscription$?.unsubscribe();
    this.closeButtonSubscription$?.unsubscribe();
    this.backgroundOverlaySubscription$?.unsubscribe();
  }

  /**
   * Sets the content of the modal.
   * This method is used to set the content of the modal when it is created, before it is rendered.
   * @param content
   * @returns {InnerModal}
   */
  setContent(content) {
    this.content = content;
    return this;
  }

  /**
   * Updates the content of the modal after it has been created.
   * @param content
   * @returns {InnerModal}
   */
  updateContent(content) {
    this.content = content;
    const modalContent = this.getNode().querySelector('[data-self-guided-tour-inner-modal-content]');
    if (modalContent) {
      modalContent.innerHTML = content;
    }
    return this;
  }

  /**
   * Configures a button in the modal with the specified text, action, and CSS class.
   * @param button
   * @param text
   * @param action
   * @param cssClass
   * @param setSubscription
   */
  static configureButton(button, text, action, cssClass, setSubscription) {
    if (!text) {
      return;
    }

    button.textContent = text;
    if (action) {
      setSubscription(fromEvent(button, 'click').subscribe(action));
      button.hidden = false;
    }
    if (cssClass) {
      button.classList.add(cssClass);
    }
  }

  /**
   * Updates the text and actions of the primary and secondary buttons in the modal.
   * @param {Object} options - The options to update the buttons.
   * @param {string} [options.primaryButtonText] - The text for the primary button.
   * @param {Function} [options.primaryButtonAction] - The action for the primary button.
   * @param {string} [options.secondaryButtonText] - The text for the secondary button.
   * @param {Function} [options.secondaryButtonAction] - The action for the secondary button.
   */
  updateButtons(options) {
    this.resetButtons();

    InnerModal.configureButton(
      this.getPrimaryButton(),
      options.primaryButtonText,
      options.primaryButtonAction,
      options.primaryButtonCssClass,
      (sub) => {
        this.primaryButtonSubscription$ = sub;
      }
    );

    InnerModal.configureButton(
      this.getSecondaryButton(),
      options.secondaryButtonText,
      options.secondaryButtonAction,
      options.secondaryButtonCssClass,
      (sub) => {
        this.secondaryButtonSubscription$ = sub;
      }
    );

    return this;
  }

  /**
   * Resets the button to its default state.
   * @param {HTMLElement} button - The button element to reset.
   * @param {string[]} defaultCssClasses - The default CSS classes to apply to the button.
   */
  static resetButton(button, defaultCssClasses) {
    if (button) {
      button.textContent = '';
      button.hidden = true;
      button.className = '';
      button.classList.add(...defaultCssClasses);
    }
  }

  /**
   * Resets the buttons in the modal to their default state.
   */
  resetButtons() {
    InnerModal.resetButton(
      this.getPrimaryButton(),
      this.primaryButtonDefaultCssClasses
    );
    if (this.primaryButtonSubscription$) {
      this.primaryButtonSubscription$.unsubscribe();
      this.primaryButtonSubscription$ = null;
    }

    InnerModal.resetButton(
      this.getSecondaryButton(),
      this.secondaryButtonDefaultCssClasses
    );
    if (this.secondaryButtonSubscription$) {
      this.secondaryButtonSubscription$.unsubscribe();
      this.secondaryButtonSubscription$ = null;
    }
  }

  /**
   * Resets the modal to its initial state.
   */
  reset() {
    this.resetButtons();
    this.updateContent('');
  }

  render() {
    return `<div class="nhs-c-overlay--fullsize nhs-u-fixed" data-self-guided-tour-inner-modal hidden>
  <div class="nhs-c-dialog__container--max-width-6750">
    <button
     type="button"
     class="nhs-c-modal__close nhs-c-icon__close--24 nhs-u-background-color--transparent"
     data-self-guided-tour-inner-modal-close
     >
      ${this.closeModalIconSvgElement}
    </button>
    <div class="nhs-c-modal__content">
      <div data-self-guided-tour-inner-modal-content></div>

      <div class="nhs-o-flex-column-items-center-self-stretch nhs-u-padding--500 nhs-u-gap--300">
        <button type="button" data-self-guided-tour-inner-modal-primary-button></button>
        <button type="button" data-self-guided-tour-inner-modal-secondary-button></button>
      </div>
    </div>
  </div>
</div>`;
  }
}
